import { createSelector } from '@reduxjs/toolkit';
import { fetchBidderBiddingAll } from '@/redux/api/biddingInfo';
import { getAuthToken } from './user';
import { getDeployment } from './config';
import { GlobalState } from '../rootReducer';
import {
    LOAD_BIDDER_BIDDING_ALL_FAIL,
    LOAD_BIDDER_BIDDING_ALL_REQUEST,
    LOAD_BIDDER_BIDDING_ALL_SUCCESS,
    LOAD_BIDDER_DATA_SUCCESS,
    LOG_OUT_BIDDER,
    LOGIN_SUCCESS,
} from './actions';
import ms from 'ms';

const REDUX_STORE_TIME = ms('15m');

export type State = {
    allBidItemIds: number[];
    leadingBidItemIds: number[];
    loaded: number;
    loading: boolean;
    pastBidItemIds: number[];
    upcomingBidItemIds: number[];
    wonBidItemIds: number[];
};

export const DEFAULT_STATE: State = {
    allBidItemIds: [],
    leadingBidItemIds: [],
    loaded: 0,
    loading: false,
    pastBidItemIds: [],
    upcomingBidItemIds: [],
    wonBidItemIds: [],
};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}): State {
    let loaded: State['loaded'];
    let loading: State['loading'];

    switch (action.type) {
        case LOAD_BIDDER_BIDDING_ALL_FAIL:
            return {
                ...state,
                loading: false,
            };
        case LOAD_BIDDER_BIDDING_ALL_REQUEST:
            return {
                ...state,
                loading: true,
            };
        case LOAD_BIDDER_BIDDING_ALL_SUCCESS:
            loaded = action.meta.actionTime;
            loading = false;
            return {
                ...state,
                allBidItemIds: action.payload.allBidItemIds,
                leadingBidItemIds: action.payload.leadingBidItemIds,
                loaded,
                loading,
                pastBidItemIds: action.payload.pastBidItemIds,
                upcomingBidItemIds: action.payload.upcomingBidItemIds,
                wonBidItemIds: action.payload.wonBidItemIds,
            };
        case LOAD_BIDDER_DATA_SUCCESS:
        case LOGIN_SUCCESS:
            return {
                ...state,
                upcomingBidItemIds: action.payload.biddingInfos.map(({ itemId }) => itemId),
            };
        case LOG_OUT_BIDDER:
            return { ...DEFAULT_STATE };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.bidderBiddingInfoAll;
const idSelector = (state: GlobalState, id) => id;
const loadedSelector = createSelector(stateSelector, (state) => state.loaded);
const loadingSelector = createSelector(stateSelector, (state) => state.loading);

const shouldFetch = createSelector([loadedSelector, loadingSelector], (loaded, loading) => {
    const time = Date.now();
    const diff = time - loaded;
    if (diff < REDUX_STORE_TIME) {
        return false;
    }
    return !loading;
});

export const getMyBidItemIds = createSelector(stateSelector, (state) => ({
    allBidItemIds: state.allBidItemIds,
    leadingBidItemIds: state.leadingBidItemIds,
    pastBidItemIds: state.pastBidItemIds,
    upcomingBidItemIds: state.upcomingBidItemIds,
    wonBidItemIds: state.wonBidItemIds,
}));

export const getWonItem = createSelector([stateSelector, idSelector], (state, id) => state.wonBidItemIds.includes(id));

/* ACTION CREATORS */
const doFetch = async ({ dispatch, getState }) => {
    const state = getState();
    try {
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        dispatch({
            meta: { actionTime: Date.now() },
            type: LOAD_BIDDER_BIDDING_ALL_REQUEST,
        });

        const { payload } = await fetchBidderBiddingAll({ authToken, deployment });

        dispatch({
            meta: { actionTime: Date.now() },
            payload,
            type: LOAD_BIDDER_BIDDING_ALL_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            meta: { actionTime: Date.now() },
            payload: error,
            type: LOAD_BIDDER_BIDDING_ALL_FAIL,
        });
    }
};

export const fetchAllBidderBiddingIfNeeded = () => async (dispatch: Function, getState: Function) => {
    const state = getState();
    const authToken = getAuthToken(state);
    if (authToken && shouldFetch(state)) {
        return doFetch({ dispatch, getState });
    }
    return Promise.resolve({});
};
